import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";

import { UserContext } from "../../utils/contexts/UserContext";
import useAuth from "../../utils/hooks/useAuth";

import SmashLoader from "../../components/Loader/SmashLoader";
import TextField from "../../components/forms/TextField/TextField";
import authPersonBackground from "../../assets/auth_person_background.svg";

export default function LoginPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const { loginUser } = useAuth();

  const { isLoggedIn, isStudent, isExpert, isUniversity, isAdmin } = useContext(UserContext);

  if (isLoading) return <SmashLoader />;

  if (isLoggedIn) {
    const destination = isStudent
      ? '/portfolio'
      : isExpert
        ? '/expert/reviews'
        : isUniversity
          ? '/organization/dashboard'
          : isAdmin
            ? '/platform'
            : '/profile';
    return <Navigate to={destination} replace />;
  }

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await loginUser(data);
    } catch (error) {
      setErrorMessage("Login failed. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] w-full flex-row items-start justify-center overflow-y-auto bg-blue-50 p-6 dark:bg-slate-900 lg:gap-16">
      <div className="flex flex-col justify-start flex-grow items-center lg:items-end w-full h-full max-h-[620px]">
        <div className="flex flex-col font-inter gap-2 items-end lg:items-center h-full">
          <div className="flex flex-col items-center justify-center flex-grow w-full max-w-96">
            <div className="rounded-2xl flex flex-col items-center gap-6 max-w-96 w-full">
              <div className="flex flex-col gap-2 w-full">
                <div className="text-center font-bold font-poppins text-zinc-800 dark:text-zinc-200 text-3xl">
                  User Log in
                </div>
                <div className="text-center text-neutral-700 dark:text-neutral-400 text-opacity-80 text-sm">
                  Sign in to access Smash.
                </div>
              </div>
              {errorMessage && (
                <p className="text-red-500 text-center">{errorMessage}</p>
              )}
              <form
                className="flex flex-col gap-4 w-full"
                onSubmit={handleSubmit(onSubmit)}
              >
                {/* Email */}
                <TextField
                  {...register("email", { required: "Email is required" })}
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  required
                  error={errors.email}
                />

                {/* Password */}
                <div className="flex flex-col w-full">
                  <TextField
                    {...register("password", {
                      required: "Password is required",
                    })}
                    label="Password"
                    name="password"
                    type="password"
                    placeholder="Password"
                    required
                    error={errors.password}
                    showPasswordToggle={true}
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword(!showPassword)}
                  />
                  <Link
                    to="/auth/forgot-password"
                    className="text-black dark:text-white text-sm underline cursor-pointer mt-1"
                  >
                    Forgot Password?
                  </Link>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full py-2 bg-blue-950 dark:bg-blue-700 text-white rounded-xl hover:opacity-80"
                >
                  Continue
                </button>
              </form>
            </div>
          </div>
          <div className="flex flex-col gap-3 pb-6 lg:pb-0 max-w-96">
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              New to Smash?{" "}
              <Link
                to="/register"
                className="text-blue-500 dark:text-blue-400 font-semibold hover:underline"
              >
                Create an account
              </Link>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              By clicking continue, you agree to our{" "}
              <Link to="#" className="underline dark:text-blue-400">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="#" className="underline dark:text-blue-400">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
      <div className="hidden flex-grow flex-row items-center justify-start flex-start w-full pt-8 lg:flex">
        <div
          className=" bg-top bg-cover rounded-xl h-[620px] flex flex-col items-center justify-center gap-20 max-w-96 py-6"
          style={{
            backgroundImage: `url("./auth_background.png")`,
          }}
        >
          <div className="flex flex-col items-center justify-center w-full px-14 gap-6 text-left font-poppins text-neutral-50">
            <p className="text-3xl font-medium w-full">
              Your Voice. Your Story. Your Future.
            </p>
            <p className="font-inter text-sm w-full">
              Your story is more than a number. Show who you really are through
              a personalized, AI-powered interview.
            </p>
          </div>
          <img
            src={authPersonBackground}
            alt="auth background"
            className="w-1/2 object-cover"
          />
        </div>
      </div>
    </div>
  );
}
